import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  CaretLeft, FloppyDisk, Copy, ClockCounterClockwise, Plus, Sparkle, ArrowClockwise,
  ArrowUp, ArrowDown, ArrowsOutSimple, ArrowsInSimple, DotsSixVertical, Trash, Eye, EyeSlash,
  CaretDown, FilmSlate, Clock, Stack, CheckCircle, WarningCircle, X,
} from "@phosphor-icons/react";
import { PromoPageHeader } from "@/promotions/components/PromoPageHeader";
import { PromoSelect } from "@/promotions/components/PromoSelect";
import { MediaImage } from "../components/MediaImage";
import { VisualPromptBuilder } from "../components/VisualPromptBuilder";
import { videoApi, creativeApi, fetchMediaMap, mediaUrl } from "../useCreative";
import {
  TRANSITIONS, SCENE_KINDS, ASPECT_RATIOS, SCENE_STATUS, newScene, scenesOf, withScenes,
  moveScene, duplicateScene, splitScene, mergeScene, fmtDuration,
} from "../videoUtils";
import { fmtRelative } from "../creativeUtils";

export default function VideoProject() {
  const { projectId } = useParams();
  const [project, setProject] = useState(null);
  const [assetUrls, setAssetUrls] = useState({}); // asset_id -> url
  const [expanded, setExpanded] = useState(null); // scene id
  const [genBusy, setGenBusy] = useState({});
  const [showVersions, setShowVersions] = useState(false);
  const [versions, setVersions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const saveTimer = useRef(null);

  const resolveAssets = useCallback(async (proj) => {
    const ids = scenesOf(proj).flatMap((s) => s.asset_ids || []);
    if (ids.length === 0) return;
    try {
      const [assets, media] = await Promise.all([creativeApi.listAssets(), fetchMediaMap()]);
      const map = {};
      assets.filter((a) => ids.includes(a.id)).forEach((a) => { map[a.id] = mediaUrl(media, a.media_asset_id); });
      setAssetUrls((m) => ({ ...m, ...map }));
    } catch { /* preview is best-effort */ }
  }, []);

  const load = useCallback(async () => {
    const p = await videoApi.getProject(projectId);
    setProject(p);
    resolveAssets(p);
  }, [projectId, resolveAssets]);
  useEffect(() => { load(); }, [load]);

  // debounced autosave (no version bump)
  const scheduleSave = useCallback((next) => {
    setDirty(true);
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        const saved = await videoApi.updateProject(projectId, {
          title: next.title, description: next.description, settings: next.settings, storyboard: next.storyboard,
        });
        setProject((cur) => ({ ...saved, storyboard: cur.storyboard, title: cur.title })); // keep local edits, refresh overview
        setProject((cur) => ({ ...cur, overview: saved.overview, version: saved.version }));
        setDirty(false);
      } catch { toast.error("Autosave failed"); }
    }, 900);
  }, [projectId]);

  const apply = (mutator) => {
    setProject((cur) => {
      const next = mutator(cur);
      scheduleSave(next);
      return next;
    });
  };

  const setScenes = (fn) => apply((p) => withScenes(p, fn(scenesOf(p))));
  const patchScene = (id, patch) => setScenes((sc) => sc.map((s) => (s.id === id ? { ...s, ...patch } : s)));

  const addScene = () => setScenes((sc) => [...sc, newScene({ n: sc.length + 1, aspect_ratio: project?.settings?.aspect_ratio })]);

  const explicitSave = async () => {
    setSaving(true);
    clearTimeout(saveTimer.current);
    try {
      await videoApi.updateProject(projectId, { title: project.title, description: project.description, settings: project.settings, storyboard: project.storyboard });
      const saved = await videoApi.saveProject(projectId);
      setProject((cur) => ({ ...cur, version: saved.version, overview: saved.overview }));
      setDirty(false);
      toast.success(`Saved — version ${saved.version}`);
    } catch { toast.error("Save failed"); }
    setSaving(false);
  };

  const duplicate = async () => { const p = await videoApi.duplicateProject(projectId); toast.success("Project duplicated"); window.location.href = `/creative/video/${p.id}`; };

  const openVersions = async () => { setShowVersions(true); setVersions(await videoApi.listVersions(projectId)); };
  const restore = async (v) => { if (!window.confirm(`Restore version ${v}? Current state is snapshotted first.`)) return; await videoApi.restoreVersion(projectId, v); toast.success(`Restored version ${v}`); setShowVersions(false); load(); };

  const generate = async (id) => {
    setGenBusy((b) => ({ ...b, [id]: true }));
    patchScene(id, { status: "generating" });
    try {
      const scene = await videoApi.generateScene(projectId, id);
      setProject((cur) => withScenes(cur, scenesOf(cur).map((s) => (s.id === id ? scene : s))));
      if (scene.status === "generated") { toast.success("Scene generated"); resolveAssets({ storyboard: { scenes: [scene] } }); }
      else toast.error(scene.error || "Scene generation failed");
    } catch { toast.error("Generation failed"); patchScene(id, { status: "failed" }); }
    setGenBusy((b) => ({ ...b, [id]: false }));
  };

  if (!project) return <div className="p-8 text-sm text-muted-foreground" data-testid="video-project-loading">Loading project…</div>;
  const scenes = scenesOf(project);
  const o = project.overview || {};

  return (
    <div className="space-y-6" data-testid="video-project">
      <Link to="/creative/video" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="video-back">
        <CaretLeft size={14} /> All projects
      </Link>

      <PromoPageHeader
        overline="AI Video Studio"
        title={project.title}
        description={project.description || "Storyboard your AI video — compose each scene with the Visual Prompt Builder and generate through the Routing Engine."}
        actions={
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground" data-testid="video-autosave-state">{dirty ? "Saving…" : "All changes saved"}</span>
            <button onClick={openVersions} className="flex items-center gap-1.5 rounded-md border border-border px-3 py-2 text-sm text-muted-foreground hover:bg-accent" data-testid="video-versions-btn">
              <ClockCounterClockwise size={14} /> v{project.version}
            </button>
            <button onClick={duplicate} className="flex items-center gap-1.5 rounded-md border border-border px-3 py-2 text-sm text-muted-foreground hover:bg-accent" data-testid="video-duplicate-btn">
              <Copy size={14} /> Duplicate
            </button>
            <button onClick={explicitSave} disabled={saving} className="flex items-center gap-1.5 rounded-md bg-ai px-4 py-2 text-sm font-medium text-ai-foreground hover:opacity-90 disabled:opacity-60" data-testid="video-save-btn">
              <FloppyDisk size={15} /> {saving ? "Saving…" : "Save version"}
            </button>
          </div>
        }
      />

      {/* Overview */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-5" data-testid="video-overview">
        <OStat Icon={Stack} label="Scenes" value={o.total_scenes ?? scenes.length} />
        <OStat Icon={CheckCircle} label="Generated" value={o.generated_scenes ?? 0} tone="text-success" />
        <OStat Icon={ArrowClockwise} label="Pending" value={o.pending_scenes ?? 0} tone="text-warning" />
        <OStat Icon={WarningCircle} label="Failed" value={o.failed_scenes ?? 0} tone="text-destructive" />
        <OStat Icon={Clock} label="Duration" value={fmtDuration(o.total_duration_seconds)} />
      </div>

      {/* Timeline (read-only) */}
      <Timeline scenes={scenes} />

      {/* Storyboard */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold tracking-tight">Storyboard</h2>
        <button onClick={addScene} className="flex items-center gap-1.5 rounded-md border border-border px-3 py-2 text-sm hover:bg-accent" data-testid="video-add-scene">
          <Plus size={15} weight="bold" /> Add Scene
        </button>
      </div>

      {scenes.length === 0 ? (
        <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed border-border py-14 text-center text-muted-foreground" data-testid="video-empty-storyboard">
          <FilmSlate size={30} weight="duotone" />
          <p className="text-sm">No scenes yet — add your first scene to start the storyboard.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="video-scenes">
          {scenes.map((s, i) => (
            <SceneCard
              key={s.id} scene={s} index={i} total={scenes.length}
              url={(s.asset_ids || []).map((id) => assetUrls[id]).find(Boolean)}
              expanded={expanded === s.id} genBusy={!!genBusy[s.id]}
              onExpand={() => setExpanded(expanded === s.id ? null : s.id)}
              onPatch={(patch) => patchScene(s.id, patch)}
              onStructured={(structured) => patchScene(s.id, { structured, status: "not_generated" })}
              onMove={(dir) => setScenes((sc) => moveScene(sc, i, dir))}
              onDuplicate={() => setScenes((sc) => duplicateScene(sc, i))}
              onSplit={() => setScenes((sc) => splitScene(sc, i))}
              onMerge={() => setScenes((sc) => mergeScene(sc, i))}
              onDelete={() => setScenes((sc) => sc.filter((x) => x.id !== s.id))}
              onGenerate={() => generate(s.id)}
            />
          ))}
        </div>
      )}

      {showVersions && (
        <VersionsDrawer versions={versions} current={project.version} onRestore={restore} onClose={() => setShowVersions(false)} />
      )}
    </div>
  );
}

function OStat({ Icon, label, value, tone }) {
  return (
    <div className="rounded-lg border border-border bg-card p-3" data-testid={`video-stat-${label.toLowerCase()}`}>
      <div className="promo-overline flex items-center gap-1"><Icon size={12} /> {label}</div>
      <div className={`mt-1 text-xl font-semibold ${tone || ""}`}>{value}</div>
    </div>
  );
}

function Timeline({ scenes }) {
  const active = scenes.filter((s) => s.enabled !== false);
  const total = active.reduce((a, s) => a + (s.duration_seconds || 0), 0) || 1;
  return (
    <div className="rounded-lg border border-border bg-card p-4" data-testid="video-timeline">
      <div className="mb-2 flex items-center justify-between">
        <span className="promo-overline">Timeline · read-only</span>
        <span className="text-xs text-muted-foreground">{fmtDuration(total)} · {active.length} active scene{active.length === 1 ? "" : "s"}</span>
      </div>
      {active.length === 0 ? (
        <p className="text-xs text-muted-foreground">Enable scenes to see them on the timeline.</p>
      ) : (
        <div className="flex h-10 w-full items-stretch gap-0.5 overflow-hidden rounded-md" data-testid="video-timeline-track">
          {active.map((s, i) => (
            <div key={s.id} title={`${s.title} · ${fmtDuration(s.duration_seconds)} · ${s.transition}`}
              style={{ width: `${((s.duration_seconds || 0) / total) * 100}%` }}
              className={`flex min-w-[28px] items-center justify-center border-r border-background text-[10px] font-medium ${s.status === "generated" ? "bg-ai/30 text-ai" : s.status === "failed" ? "bg-destructive/20 text-destructive" : "bg-muted text-muted-foreground"}`}
              data-testid={`video-timeline-scene-${i}`}>
              {i + 1}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SceneCard({ scene: s, index, total, url, expanded, genBusy, onExpand, onPatch, onStructured, onMove, onDuplicate, onSplit, onMerge, onDelete, onGenerate }) {
  const status = SCENE_STATUS[s.status] || SCENE_STATUS.not_generated;
  return (
    <div className={`rounded-lg border bg-card p-4 ${s.enabled === false ? "border-border opacity-60" : "border-border"}`} data-testid={`video-scene-${s.id}`}>
      <div className="flex gap-4">
        {/* Preview */}
        <div className="relative h-24 w-32 flex-shrink-0 overflow-hidden rounded-md border border-border bg-muted/40" data-testid={`video-scene-preview-${s.id}`}>
          {url ? <MediaImage url={url} alt={s.title} className="h-full w-full object-cover" /> : (
            <div className="flex h-full w-full items-center justify-center text-muted-foreground"><FilmSlate size={22} weight="duotone" /></div>
          )}
          <span className="absolute left-1 top-1 rounded bg-background/80 px-1.5 py-0.5 text-[10px] font-medium">#{index + 1}</span>
        </div>

        {/* Meta + controls */}
        <div className="flex-1 space-y-2">
          <div className="flex items-center gap-2">
            <input value={s.title} onChange={(e) => onPatch({ title: e.target.value })} className="flex-1 rounded-md border border-transparent bg-transparent px-1 py-0.5 text-sm font-medium outline-none hover:border-border focus:border-ai" data-testid={`video-scene-title-${s.id}`} />
            <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${status.tone}`} data-testid={`video-scene-status-${s.id}`}>{status.label}</span>
          </div>

          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            <Mini label="Kind"><PromoSelect value={s.kind} onChange={(v) => onPatch({ kind: v, status: "not_generated" })} options={SCENE_KINDS} testId={`video-scene-kind-${s.id}`} /></Mini>
            <Mini label="Duration (s)"><input type="number" min="1" value={s.duration_seconds} onChange={(e) => onPatch({ duration_seconds: Number(e.target.value) })} className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm" data-testid={`video-scene-duration-${s.id}`} /></Mini>
            <Mini label="Aspect"><PromoSelect value={s.aspect_ratio} onChange={(v) => onPatch({ aspect_ratio: v })} options={ASPECT_RATIOS.map((r) => ({ value: r, label: r }))} testId={`video-scene-aspect-${s.id}`} /></Mini>
            <Mini label="Transition"><PromoSelect value={s.transition} onChange={(v) => onPatch({ transition: v })} options={TRANSITIONS.map((t) => ({ value: t, label: t }))} testId={`video-scene-transition-${s.id}`} /></Mini>
          </div>

          <div className="flex flex-wrap items-center gap-1">
            <SBtn Icon={ArrowUp} title="Move up" disabled={index === 0} onClick={() => onMove(-1)} testid={`video-scene-up-${s.id}`} />
            <SBtn Icon={ArrowDown} title="Move down" disabled={index === total - 1} onClick={() => onMove(1)} testid={`video-scene-down-${s.id}`} />
            <SBtn Icon={Copy} title="Duplicate" onClick={onDuplicate} testid={`video-scene-dup-${s.id}`} />
            <SBtn Icon={ArrowsOutSimple} title="Split" onClick={onSplit} testid={`video-scene-split-${s.id}`} />
            <SBtn Icon={ArrowsInSimple} title="Merge with next" disabled={index === total - 1} onClick={onMerge} testid={`video-scene-merge-${s.id}`} />
            <SBtn Icon={s.enabled === false ? EyeSlash : Eye} title={s.enabled === false ? "Enable" : "Disable"} onClick={() => onPatch({ enabled: !(s.enabled !== false) })} testid={`video-scene-toggle-${s.id}`} />
            <SBtn Icon={Trash} title="Delete" danger onClick={onDelete} testid={`video-scene-del-${s.id}`} />
            <div className="flex-1" />
            <button onClick={onExpand} className="flex items-center gap-1 rounded-md border border-border px-2.5 py-1 text-xs hover:bg-accent" data-testid={`video-scene-edit-${s.id}`}>
              <CaretDown size={12} className={`transition-transform ${expanded ? "rotate-180" : ""}`} /> Prompt
            </button>
            <button onClick={onGenerate} disabled={genBusy} className="flex items-center gap-1.5 rounded-md bg-ai px-3 py-1 text-xs font-medium text-ai-foreground hover:opacity-90 disabled:opacity-60" data-testid={`video-scene-generate-${s.id}`}>
              {genBusy ? <ArrowClockwise size={13} className="animate-spin" /> : <Sparkle size={13} weight="fill" />} Generate
            </button>
          </div>
          {s.error && <p className="text-xs text-destructive" data-testid={`video-scene-error-${s.id}`}>{s.error}</p>}
        </div>
      </div>

      {expanded && (
        <div className="mt-4 space-y-3 border-t border-border pt-4">
          <VisualPromptBuilder structured={s.structured || {}} kind={s.kind} onChange={onStructured} />
          <div>
            <label className="promo-overline">Scene notes</label>
            <textarea rows={2} value={s.notes} onChange={(e) => onPatch({ notes: e.target.value })} placeholder="Direction, continuity notes…" className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm" data-testid={`video-scene-notes-${s.id}`} />
          </div>
        </div>
      )}
    </div>
  );
}

function Mini({ label, children }) {
  return <div><label className="promo-overline mb-1 block">{label}</label>{children}</div>;
}
function SBtn({ Icon, title, onClick, disabled, danger, testid }) {
  return (
    <button onClick={onClick} disabled={disabled} title={title} data-testid={testid}
      className={`flex h-7 w-7 items-center justify-center rounded-md hover:bg-accent disabled:opacity-30 ${danger ? "text-muted-foreground hover:text-destructive" : "text-muted-foreground hover:text-foreground"}`}>
      <Icon size={14} />
    </button>
  );
}

function VersionsDrawer({ versions, current, onRestore, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40" onClick={onClose} data-testid="video-versions-drawer">
      <div className="h-full w-full max-w-sm overflow-auto border-l border-border bg-card p-5" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-base font-semibold">Version History</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X size={16} /></button>
        </div>
        <p className="mb-3 text-xs text-muted-foreground">Current: v{current}. Restoring snapshots the current state first, so it's always reversible.</p>
        {versions.length === 0 ? <p className="text-sm text-muted-foreground">No saved versions yet — click “Save version”.</p> : (
          <ul className="space-y-2">
            {versions.map((v) => (
              <li key={v.id} className="flex items-center justify-between rounded-md border border-border p-3" data-testid={`video-version-${v.version}`}>
                <div>
                  <div className="text-sm font-medium">Version {v.version}</div>
                  <div className="text-xs text-muted-foreground">{fmtRelative(v.created_at)} · {(v.snapshot?.storyboard?.scenes || []).length} scenes</div>
                </div>
                <button onClick={() => onRestore(v.version)} className="rounded-md border border-border px-2.5 py-1 text-xs hover:bg-accent" data-testid={`video-restore-${v.version}`}>Restore</button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
